import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import BodyText from 'LuxKit/Typography/BodyText'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import Clickable from 'components/Common/Clickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Expander from 'components/utils/Expander'
import { arrayToObject, unique, without } from 'lib/array/arrayUtils'
import { EXP_CATEGORY_DAY_TRIPS_ID } from 'constants/experience'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useTrackExperienceSearchFilter from 'components/Experiences/hooks/useTrackExperienceSearchFilter'
import useMostPopulatedSearchExperienceFacets from '../hooks/useMostPopulatedSearchExperienceFacets'
import {
  getNextCategoryCodesForSearchCategory,
  isTravelEssentialsCategory,
  splitTravelEssentialsCategoryGroups,
} from './experienceSearchTravelEssentialsSplit'

const ListContainer = styled.div`
  margin-top: ${rem(6)};
`

const FilterItem = styled.div`
  & + & {
    margin-top: ${rem(8)};
  }
`

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${rem(24)};
  align-items: center;
  column-gap: ${rem(4)};
`

const ExpandButton = styled(Clickable)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${rem(24)};
  height: ${rem(24)};
`

const ChildList = styled.div`
  margin-inline-start: ${rem(36)};
  padding-block: ${rem(8)};
`

const CountText = styled(BodyText)`
  margin-inline-start: ${rem(4)};
`

interface Props {
  place?: App.Place
}

function ExperienceSearchTravelEssentialsFilter({ place }: Props) {
  const intl = useIntl()
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()
  const trackSearchFilter = useTrackExperienceSearchFilter()
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<number>>(() => new Set())
  const { primaryCategories, categories } = useMostPopulatedSearchExperienceFacets(place, {
    rankCategoriesByRelevance: true,
    prioritiseSelectedCategories: true,
  })

  const categoryFacetsById = useMemo(
    () => arrayToObject(categories, (facet) => facet.categoryId),
    [categories],
  )

  const labels = useMemo(() => ({
    transfers: intl.formatMessage({
      id: 'WGj8M709yLcgGiD9hdij',
      defaultMessage: 'Transfers',
      description: 'Experience search category label for transfer-related travel essentials',
    }),
    loungesAndServices: intl.formatMessage({
      id: 'x8JnpWwJHaV5vW0mX-B0',
      defaultMessage: 'Lounges and Services',
      description: 'Experience search category label for lounge and airport service travel essentials',
    }),
  }), [intl])

  const groups = useMemo(
    () => splitTravelEssentialsCategoryGroups(
      primaryCategories.filter((category) => isTravelEssentialsCategory(category.parent)),
      labels,
      categoryFacetsById,
    ),
    [categoryFacetsById, labels, primaryCategories],
  )

  const selectedCategoryCodes = useMemo(
    () => searchFilters.categoryCodes ?? [],
    [searchFilters.categoryCodes],
  )

  const normalisedSelectedCategoryCodes = useMemo(() => {
    if (!selectedCategoryCodes.includes(EXP_CATEGORY_DAY_TRIPS_ID)) {
      return selectedCategoryCodes
    }

    return [
      ...without(selectedCategoryCodes, EXP_CATEGORY_DAY_TRIPS_ID),
      ...groups.flatMap((group) => group.children.map((child) => child.id)),
    ]
  }, [groups, selectedCategoryCodes])

  const isGroupSelected = useCallback((group: typeof groups[number]) => {
    const childIds = group.children.map((child) => child.id)
    return selectedCategoryCodes.includes(EXP_CATEGORY_DAY_TRIPS_ID) ||
      (childIds.length > 0 && childIds.every((id) => normalisedSelectedCategoryCodes.includes(id)))
  }, [normalisedSelectedCategoryCodes, selectedCategoryCodes])

  const onToggleGroup = useCallback((group: typeof groups[number]) => {
    const childIds = group.children.map((child) => child.id)
    const selected = isGroupSelected(group)
    const categoryCodes = getNextCategoryCodesForSearchCategory(
      normalisedSelectedCategoryCodes,
      group.parent.id,
      childIds,
      selected ? [] : childIds,
    )

    trackSearchFilter({
      label: group.parent.name,
      type: 'theme_category',
      value: selected ? 'unselected' : 'selected',
    })
    setSearchFilters({
      ...searchFilters,
      categoryCodes: categoryCodes.length > 0 ? categoryCodes : undefined,
    })
  }, [isGroupSelected, normalisedSelectedCategoryCodes, searchFilters, setSearchFilters, trackSearchFilter])

  const onToggleChild = useCallback((group: typeof groups[number], childId: number) => {
    const childIds = group.children.map((child) => child.id)
    const selected = normalisedSelectedCategoryCodes.includes(childId)
    const selectedChildIds = childIds.filter((id) => normalisedSelectedCategoryCodes.includes(id))
    const nextSelectedChildIds = selected
      ? without(selectedChildIds, childId)
      : unique([...selectedChildIds, childId])
    const categoryCodes = getNextCategoryCodesForSearchCategory(
      normalisedSelectedCategoryCodes,
      group.parent.id,
      childIds,
      nextSelectedChildIds,
    )
    const child = group.children.find((item) => item.id === childId)

    trackSearchFilter({
      label: child?.name ?? String(childId),
      type: 'theme_subcategory',
      value: selected ? 'unselected' : 'selected',
    })
    setSearchFilters({
      ...searchFilters,
      categoryCodes: categoryCodes.length > 0 ? categoryCodes : undefined,
    })
  }, [normalisedSelectedCategoryCodes, searchFilters, setSearchFilters, trackSearchFilter])

  const onToggleExpanded = useCallback((groupId: number) => {
    setExpandedGroupIds((current) => {
      const next = new Set(current)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }, [])

  const visibleGroups = groups.filter((group) => group.count > 0 || isGroupSelected(group))

  return (
    <ListContainer>
      {visibleGroups.map((group) => {
        const selected = isGroupSelected(group)
        const visibleChildren = group.children.filter((child) =>
          (categoryFacetsById[child.id]?.count ?? 0) > 0 || normalisedSelectedCategoryCodes.includes(child.id),
        )
        const indeterminate = !selected && visibleChildren.some((child) => normalisedSelectedCategoryCodes.includes(child.id))
        const expanded = expandedGroupIds.has(group.parent.id) || indeterminate

        return <FilterItem key={group.parent.id}>
          <FilterRow>
            <CheckboxInput
              size="medium"
              checked={selected}
              indeterminate={indeterminate}
              onChange={() => onToggleGroup(group)}
            >
              <BodyText as="span" variant="medium">{group.parent.name}</BodyText>
            </CheckboxInput>
            {visibleChildren.length > 0 && <ExpandButton
              onClick={() => onToggleExpanded(group.parent.id)}
              aria-expanded={expanded}
              aria-label={group.parent.name}
            >
              <DropdownChevron open={expanded} />
            </ExpandButton>}
          </FilterRow>
          <Expander expanded={expanded}>
            <ChildList>
              <VerticalSpacer gap={8}>
                {visibleChildren.map((child) => <CheckboxInput
                  key={child.id}
                  size="medium"
                  checked={normalisedSelectedCategoryCodes.includes(child.id)}
                  onChange={() => onToggleChild(group, child.id)}
                >
                  <BodyText as="span" variant="medium">
                    {child.name}
                    <CountText as="span" variant="medium" colour="neutral-two">
                      {intl.formatMessage(
                        {
                          id: 'EfkvoARy0J9lyukUXTWh',
                          defaultMessage: '({count})',
                          description:
                            'Count of matching experiences shown next to a theme filter option',
                        },
                        { count: categoryFacetsById[child.id]?.count ?? 0 },
                      )}
                    </CountText>
                  </BodyText>
                </CheckboxInput>)}
              </VerticalSpacer>
            </ChildList>
          </Expander>
        </FilterItem>
      })}
    </ListContainer>
  )
}

export default ExperienceSearchTravelEssentialsFilter
